import { ReactElement } from 'react';

import { Timestamp } from 'firebase/firestore';

import Link from '@mui/material/Link';

import dayjs from 'dayjs';

import Event from '../../types/Event';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { resolveDefaultPlayerChoiceLock, resolvePlayerChoiceLock } from '../../utils/playerChoiceLock';
import GratiDateTimeField from '../common/GratiDateTimeField';

interface EventPlayerChoiceLockFieldProps {
  event: Event;
  onUpdate: (updates: Partial<Event>) => void;
}

export const PLAYER_CHOICE_LOCK_LABEL = 'Player choices close';
export const PLAYER_CHOICE_LOCK_FORMAT = 'MMM D, YYYY h:mm A';

/**
 * When players' own choices close — holes, walk/ride, games and tees (Event.playerChoiceLockDate).
 * Until the admin sets it, it follows the Handicap Index date: the end of that day (the server keeps
 * the stored value in step — see resolveDefaultPlayerChoiceLock). Setting a date or time marks it
 * as set by hand; "Follow the Handicap Index date" goes back. An event from before the lock date
 * existed has none stored and closes at the first round's start until its dates are next edited.
 * In the event editor and the tee sheet settings.
 */
export default function EventPlayerChoiceLockField({ event, onUpdate }: EventPlayerChoiceLockFieldProps): ReactElement {
  const isManual = !!event.playerChoiceLockDateManuallySet;
  const isStored = event.playerChoiceLockDate instanceof Timestamp;
  const isNewEvent = !event.itemId;
  const defaultMs = resolveDefaultPlayerChoiceLock(event);
  // Computed while following, so the field moves with the Handicap Index date as it's edited,
  // before the server stores it
  const isFollowing = !isManual && (isStored || isNewEvent);
  const valueMs = isFollowing ? defaultMs : resolvePlayerChoiceLock(event);
  const followsLabel = event.ghinIndexDate instanceof Timestamp ? `the ${HANDICAP_LABELS.INDEX_DATE}` : 'the day before the first round';

  const note = isManual ? (
    <Link
      component="button"
      variant="caption"
      onClick={() => onUpdate({
        playerChoiceLockDateManuallySet: false,
        ...(defaultMs !== undefined ? { playerChoiceLockDate: Timestamp.fromMillis(defaultMs) } : {}),
      })}
    >
      Follow {followsLabel}
    </Link>
  ) : isFollowing ? `Follows ${followsLabel}` : "The first round's start";

  const firstRound = event.days[0]?.datetime;
  return (
    <GratiDateTimeField
      label={PLAYER_CHOICE_LOCK_LABEL}
      value={valueMs !== undefined ? Timestamp.fromMillis(valueMs) : null}
      timezone={event.timezone}
      defaultTime="end"
      maxDate={firstRound instanceof Timestamp ? dayjs(firstRound.toDate()) : undefined}
      onChange={(date) => onUpdate({ playerChoiceLockDate: date, playerChoiceLockDateManuallySet: true })}
      note={note}
    />
  );
}
